import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { FlatTreeControl, NestedTreeControl } from '@angular/cdk/tree';
import {
    Component,
    ElementRef,
    EventEmitter,
    inject,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTreeFlatDataSource, MatTreeFlattener, MatTreeNestedDataSource } from '@angular/material/tree';
import { IAppRequest, TreeNodeRegister } from '@app/app.interface';
import { AppDialogService } from '@components/dialog/services/app-dialog.service';
import { LANG_DICTIONARY_TOKEN } from '@constants/diConstant';
import { mbHasMatch } from '@fui/fui-helpers';
import { getFactorIcon } from '@modules/factor/helpers/factor-icon.helper';
import { FuiDestroyService } from '@services/destroy.service';
import { NotifierService } from '@services/notifier.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { InterfaceSettingsUserTree } from '../../table.interface';
import { SettingsTableNameDialogComponent } from '../settings-table-name/settings.table.name.dialog.component';
import { TreeNode } from './interfaces/settings.table.dialog.interface';

const getLevel = (node: TreeNodeRegister) => node.level;

const isExpandable = (node: TreeNodeRegister) => node.expandable;

const getChildren = (node: TreeNode): Observable<TreeNode[]> => of(node.children);

const transformer = (node: TreeNode, level: number): TreeNode => {
    const flatNode = node;
    flatNode.expandable = !!node.children && node.children.length > 0;
    flatNode.level = level;
    return flatNode;
};

/**
 * Глубокое копирование списка полей таблицы
 * @param list - список полей таблицы
 */
const deepCopyList = (list: any[]) => {
    return (list ?? [])
        .filter((item) => !!item)
        .map((item) => {
            const newItem = { ...item };

            if (item.hasOwnProperty('children') && Array.isArray(item.children) && item.children.length) {
                newItem.children = deepCopyList(item.children ?? []);
            }

            if (item.hasOwnProperty('columns') && Array.isArray(item.columns) && item.columns.length) {
                newItem.columns = deepCopyList(item.columns ?? []);
            }

            return newItem;
        });
};

/**
 * Компонент диалога "Настройка таблицы"
 */
@Component({
    selector: 'app-settings-table-dialog',
    providers: [FuiDestroyService],
    templateUrl: './settings.table.dialog.component.html',
    styleUrls: [
        '../settings-table-name/styles/settings.table.dialog.component.scss',
        '../settings-table-name/styles/settings.table.dialog.component.adaptive.scss',
    ],
})
export class SettingsTableDialogComponent implements OnInit, OnDestroy {
    private readonly destroy$ = inject(FuiDestroyService);
    private readonly notifier = inject(NotifierService);
    private readonly spinner = inject(NgxSpinnerService);
    private readonly dialog = inject(MatDialog);
    private readonly dialogConfirm = inject(AppDialogService);
    private readonly dialogRef = inject(MatDialogRef<SettingsTableDialogComponent>);
    dictionary = inject(LANG_DICTIONARY_TOKEN);

    data: {
        tableService: any;
        dynamicFilter: any;
        tableModule: string;
        dynamicSystemFields: string[];
        title: string;
        settings: InterfaceSettingsUserTree;
    } = inject(MAT_DIALOG_DATA);

    /** Контейнер списка "Все" */
    @ViewChild('scrollableContainerAll', { read: ElementRef }) public scrollableContainerAll;

    /** Контейнер списка "Выбрано" */
    @ViewChild('scrollableContainerSelect', { read: ElementRef }) public scrollableContainerSelect;

    /** Элементы списка "Все" */
    @ViewChildren('itemRefDataAll') public itemRefDataAll: QueryList<ElementRef>;

    /** Элементы списка "Выбрано" */
    @ViewChildren('itemRefDataSelect') public itemRefDataSelect: QueryList<ElementRef>;

    /** Состояние списков */
    filterState = {
        all: {
            index: -1,
            disableDown: true,
            disableUp: true,
        },
        select: {
            index: -1,
            disableDown: true,
            disableUp: true,
        },
    };

    isLoaded$ = new BehaviorSubject<boolean>(false);

    /** Активный узел */
    public activeNode: string;

    /** Текст, который нужно подсветить в названии узлов */
    public highlightText: { all: string; select: string } = {
        all: '',
        select: '',
    };

    /** Объект для компановки контейнера и элементов списка */
    public itemsRefData: { all: any; select: any } = {
        all: {
            container: null,
            items: [],
        },
        select: {
            container: null,
            items: [],
        },
    };

    /**
     * Дерево "Выбрано"
     */
    treeControlSelect = new NestedTreeControl<TreeNode>((node) => node.children);

    /**
     * Данные дерева "Выбрано"
     */
    dataSourceSelect = new MatTreeNestedDataSource<TreeNode>();

    /**
     * Дерево "Все"
     */
    treeControlAll = new FlatTreeControl<TreeNode>(getLevel, isExpandable);

    /**
     * Данные дерева "Все"
     */
    dataSourceAll = new MatTreeFlatDataSource<TreeNode, TreeNode>(
        this.treeControlAll,
        new MatTreeFlattener(transformer, getLevel, isExpandable, getChildren),
    );

    /**
     * Флаг перетаскивания
     * @var {boolean}
     */
    dragging = false;

    /**
     * Данные дерева "Выбрано"
     */
    public dataSelected = [];

    /**
     * Данные дерева "Все"
     */
    public dataAll = [];

    /**
     * Модель для чекбоксов
     */
    public nodeCheckbox: object = {};

    @Output() isSaveEvent = new EventEmitter<any>();

    public refreshNodeCheckbox(): void {
        this.dataSelected.forEach((select) => {
            if (select?.alias) {
                this.nodeCheckbox[select?.alias] = false;
            }
        });
    }

    firstLevel = (_: number, node: TreeNode) => node.level === 0;

    secondLevel = (_: number, node: TreeNode) => node.level === 1;

    getFactorIcon = (node: TreeNode) => getFactorIcon(node.type);

    /**
     * Определение факта включения менее двух чекбоксов
     * используется для проверки возможности объединения
     */
    hasNodeCheckboxChecked(): boolean {
        let count = 0;
        for (const checkbox in this.nodeCheckbox) {
            if (this.nodeCheckbox.hasOwnProperty(checkbox) && this.nodeCheckbox[checkbox]) {
                count++;
                // Если уже два, то дальше не идем
                if (count >= 2) {
                    return false;
                }
            }
        }
        return true;
    }

    /** Событие окончания инициализации компонента */
    ngOnInit() {
        this.dataSelected = deepCopyList(this.data.settings.select);
        this.dataAll = deepCopyList(this.data.settings.all);
        this.isLoaded$.next(true);

        this.refreshTree();

        setTimeout(() => {
            this.itemsRefData = {
                all: {
                    container: this.scrollableContainerAll?.nativeElement,
                    items: this.itemRefDataAll,
                },
                select: {
                    container: this.scrollableContainerSelect?.nativeElement,
                    items: this.itemRefDataSelect,
                },
            };
        });
    }

    public ngOnDestroy(): void {
        this.isLoaded$.complete();
    }

    /**
     * Фильтрация данных дерева
     * @param data - список узлов
     * @param search - строка поиска
     * @param dataType - тип списка узлов
     */
    public filterService(data: TreeNode[], search: string, dataType: 'all' | 'select') {
        this.filterState[dataType].index = -1;

        if (!data) {
            return;
        }
        // Организация фильтрованного массива
        let dataTree = [];
        // Получение значения строки поиска
        if (!search) {
            dataTree = data;
            this.highlightText[dataType] = '';
            this.filterState[dataType].disableUp = true;
            this.filterState[dataType].disableDown = true;
            this.activeNode = null;
        } else {
            this.filterState[dataType].disableUp = true;
            this.filterState[dataType].disableDown = false;
            this.highlightText[dataType] = search;

            data.forEach((parent) => {
                if (dataType === 'all') {
                    const element: TreeNode = { ...parent };
                    element.children = (parent.children ?? [])
                        .filter((x) => mbHasMatch(x.name, search))
                        .map((x) => ({ ...x }));
                    if (element.children.length > 0) {
                        dataTree.push(element);
                    }
                } else {
                    if (mbHasMatch(parent.name, search)) {
                        dataTree.push(parent);
                    }
                }
            });
        }
        if (dataType === 'select') {
            this.dataSourceSelect.data = dataTree;
            this.treeControlSelect.dataNodes = this.dataSourceSelect.data;
            this.treeControlSelect.expandAll();
        } else {
            this.dataSourceAll.data = dataTree;
            this.treeControlAll.dataNodes = this.dataSourceAll.data;
            if (!!search) {
                this.treeControlAll.expandAll();
            }
        }
    }

    /**  Сохранение данных */
    save() {
        this.data.settings.select = this.dataSelected;
        this.data.settings.all = this.dataAll;
        this.dialogRef.close({
            save: true,
            settings: this.data.settings,
        });
    }

    /** Закрытие диалога */
    close() {
        this.dialogRef.close({ save: false });
    }

    /**
     * Удаление ветки из дерева
     * @param treeRemove дерево из которого переносят
     * @param treeAdd дерево в которое переносят
     * @param node ветка дерева
     * @param index - индекс, вставки, что бы переместить в конкретное место
     */
    moveNodeBetweenTree(treeRemove: TreeNode[], treeAdd: TreeNode[], node: TreeNode, index: number = null) {
        if (treeRemove === this.dataSelected && treeRemove.length === 1) {
            return;
        }
        node.is_sticky = false;
        node.is_main = false;
        let parent_node;
        let children;
        const tree = [];
        treeRemove.forEach((parent) => {
            children = [];
            if (treeRemove === this.dataAll) {
                if (!parent.children) {
                    parent.children = [];
                }
                parent.children.forEach((child) => {
                    if (node.alias === child.alias) {
                        parent_node = parent;
                        node.parent = parent.name;
                    } else {
                        children.push(child);
                    }
                });
                parent.children = children;
            } else {
                if (parent.alias === node.alias) {
                    parent_node = parent;
                    node.level = 2;
                } else {
                    tree.push(parent);
                }
            }
        });
        if (tree.length > 0) {
            this.dataSelected = tree;
        }
        if (treeAdd === this.dataAll) {
            treeAdd.forEach((parent) => {
                if (parent_node.columns.length === 0 && parent.name === parent_node.parent) {
                    if (!parent.children) {
                        parent.children = [];
                    }
                    parent.children.push(node);
                }
                if (parent_node.columns.length > 1) {
                    parent_node.columns.forEach((column) => {
                        if (parent.name === column.parent) {
                            if (!parent.children) {
                                parent.children = [];
                            }
                            column.level = 2;
                            parent.children.push(column);
                        }
                    });
                }
            });
        } else {
            if (index !== null) {
                treeAdd.splice(index, 0, node);
            } else {
                treeAdd.push(node);
            }
        }
        this.refreshTree();
    }

    /**
     * Перемещение ветки по дереву
     * @param node ветка дерева
     * @param up направление движения ветки
     */
    moveNode(node: TreeNode, up: boolean) {
        let element_node;
        let element_index = 0;
        this.dataSelected.forEach((parent, index) => {
            if (node === parent) {
                element_node = parent;
                element_index = index;
            }
        });
        if (element_node) {
            if (up && element_index > 0) {
                this.dataSelected[element_index] = this.dataSelected[element_index - 1];
                this.dataSelected[element_index - 1] = element_node;
            } else if (!up && element_index < this.dataSelected.length - 1) {
                this.dataSelected[element_index] = this.dataSelected[element_index + 1];
                this.dataSelected[element_index + 1] = element_node;
            }
        }
        this.refreshTree();
    }

    /**
     * Обновление деревьев
     */
    public refreshTree(): void {
        this.refreshNodeCheckbox();

        if (this.highlightText.select?.length > 0) {
            this.filterService(this.dataSelected, this.highlightText.select, 'select');
        } else {
            this.dataSourceSelect.data = this.dataSelected;
        }

        if (this.highlightText.all?.length > 0) {
            this.filterService(this.dataAll, this.highlightText.all, 'all');
        } else {
            this.dataSourceAll.data = this.dataAll;
        }
    }

    /**
     * Обработка перетаскивания
     * @param event событие перетаскивания
     */
    drop(event: CdkDragDrop<string[]>) {
        switch (event.previousContainer.id) {
            case 'fields-tree':
                this.moveNodeBetweenTree(this.dataAll, this.dataSelected, event.item.data, event.currentIndex);
                break;
            case 'table-fields-tree':
                this.dataSelected.splice(event.previousIndex, 1);
                this.dataSelected.splice(event.currentIndex, 0, event.item.data);
                this.refreshTree();
                break;
        }
    }

    /**
     * Начало перетаскивания
     */
    dragStart() {
        this.dragging = true;
    }

    /**
     * Конец перетаскивания
     */
    dragEnd() {
        this.dragging = false;
    }

    /** Объединение колонок */
    unionNode() {
        const unionColumns = [];
        for (const checkbox in this.nodeCheckbox) {
            if (this.nodeCheckbox.hasOwnProperty(checkbox) && this.nodeCheckbox[checkbox]) {
                unionColumns.push(checkbox);
            }
        }
        this.openNameDialog('union', null, unionColumns);
    }

    /**
     * Разъединить колонки
     * @param node ветка дерева
     */
    unUnionNode(node: TreeNode) {
        const settingsSelect = [];
        this.dataSelected.forEach((select) => {
            if (select !== node) {
                settingsSelect.push(select);
            }
        });
        let idx = this.dataSelected.findIndex((x) => x.alias === node.alias);
        node.columns.forEach((column) => {
            settingsSelect.splice(idx++, null, column);
        });
        this.dataSelected = settingsSelect;
        this.refreshTree();
    }

    /**
     * Открытие диалога задания имени
     * @param type тип
     * @param node ветка дерева
     * @param unionColumns колонки для объединения
     * @param nodeRef
     */
    openNameDialog(type: string, node: TreeNode = null, unionColumns: string[] = null) {
        const dialogRef = this.dialog.open(SettingsTableNameDialogComponent, {
            disableClose: true,
            autoFocus: false,
            width: '600px',
            data: {
                title: this.dictionary.system_settings_table_dialog_title,
                name: node ? node.name : '',
                type,
            },
        });
        dialogRef
            .keydownEvents()
            .pipe(takeUntil(this.destroy$))
            .subscribe((result) => {
                if (result.code === 'Escape') {
                    dialogRef.close({ save: false });
                }
            });
        dialogRef
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((result) => {
                if (result.save) {
                    if (type === 'rename') {
                        node.name = result.name;
                        this.dataSelected = JSON.parse(JSON.stringify(this.dataSelected));
                        this.refreshTree();
                    }
                    if (type === 'union') {
                        const settingsSelect = [];
                        const unionColumnsNode = [];
                        this.dataSelected.forEach((select) => {
                            if (unionColumns.indexOf(select.alias) === -1) {
                                settingsSelect.push(select);
                            } else {
                                unionColumnsNode.push(select);
                            }
                        });
                        if (unionColumnsNode.length === 0) {
                            return;
                        }
                        const unionFirst = unionColumnsNode[0];
                        const unionFirstIndex = this.dataSelected.findIndex((x) => x.alias === unionFirst.alias);
                        this.dataSelected = settingsSelect;
                        this.dataSelected.splice(unionFirstIndex, null, {
                            parent: '',
                            is_sticky: false,
                            is_main: false,
                            type: this.getTypeUnionColumns(unionColumnsNode),
                            width: 150,
                            name: result.name,
                            alias: unionColumns.join('_'),
                            columns: unionColumnsNode,
                            level: 1,
                            isUnion: true,
                        });
                        this.refreshTree();
                    }
                    this.nodeCheckbox = [];
                }
            });
    }

    /**
     * Установка флага основной колонки
     * @param node - данные колонки
     */
    public setMainColumn(node: TreeNode): void {
        this.dataSelected.forEach((select) => {
            select.is_main = node.alias === select.alias;
        });
    }

    /**
     * Очистка настроек таблицы
     */
    clearTableSettings() {
        this.dialogConfirm
            .confirm(this.dictionary.system_confirm_action, this.dictionary.system_reset_table_settings_modal)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe((res) => {
                if (res) {
                    this.spinner.show();
                    let dataSettings: {
                        module?: string;
                    } = {};
                    if (this.data.dynamicFilter) {
                        dataSettings = Object.assign(dataSettings, this.data.dynamicFilter);
                    }
                    if (this.data.tableModule) {
                        dataSettings.module = this.data.tableModule;
                    }
                    this.data.tableService
                        .clearTableSettings(dataSettings)
                        .toPromise()
                        .then((result: IAppRequest) => {
                            this.spinner.hide();
                            if (result && result.is_error === false) {
                                this.notifier.notify('success', this.dictionary.system_reset_table_settings_success);
                                location.reload();
                            } else {
                                this.notifier.notify('error', result.message);
                            }
                        })
                        .catch(() => {
                            this.spinner.hide();
                            this.notifier.notify('error', this.dictionary.system_no_connection_with_system);
                        });
                }
            });
    }

    /**
     * Прокрутка поиска по списку
     * @param count - ступень прокрутки
     * @param typeTree - тип списка
     */
    public switchRowInFilteredData(count: -1 | 1, typeTree: 'all' | 'select'): void {
        let data = [];
        this.filterState[typeTree].index += count;

        if (typeTree === 'all') {
            this.dataSourceAll.data.forEach((item) => {
                item?.children?.forEach((child) => {
                    data.push(child);
                });
            });
        } else {
            data = this.dataSourceSelect.data;
        }

        if (this.highlightText[typeTree]?.length > 0) {
            this.filterState[typeTree].disableDown = this.filterState[typeTree].index + 1 >= data.length;
            this.filterState[typeTree].disableUp = this.filterState[typeTree].index < 1;
        }

        const current = data[this.filterState[typeTree].index];

        this.itemsRefData[typeTree].items.forEach((item) => {
            if (current.alias === item.nativeElement.id) {
                this.itemsRefData[typeTree].container.scroll({
                    top: this.scrollingToElement(item.nativeElement, this.itemsRefData[typeTree].container),
                    behavior: 'smooth',
                });
            }
        });

        this.activeNode = current?.alias;
    }

    /**
     * Прокрутка блока до активного элемента
     * @param item - параметры активного элемента
     * @param container - объект блока
     */
    public scrollingToElement(item: HTMLElement, container: HTMLElement): number {
        return item.offsetTop >= container.offsetHeight
            ? item.offsetTop - (item.offsetHeight * 2 + 20)
            : item.offsetTop - item.offsetHeight * 8;
    }

    /**
     * Определить тип, для колонки которую объединили
     * @param unionColumnsNode - объединяемые колонки
     */
    public getTypeUnionColumns(unionColumnsNode: any[]): string {
        if (
            unionColumnsNode.some((column) => column.type === 'gallery') ||
            unionColumnsNode.some((column) => column.type === 'file') ||
            unionColumnsNode.some((column) => column.type === 'HtmlTextType')
        ) {
            return 'HtmlTextType';
        }
        if (unionColumnsNode.some((column) => column.type === 'link')) {
            return 'link';
        }
        return 'string';
    }
}
